import { Box, Tooltip } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RailNavItemDef } from "./railNavItems";

interface RailNavItemProps {
  item: RailNavItemDef;
  expanded: boolean;
}

export default function RailNavItem({ item, expanded }: RailNavItemProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { customerId } = useParams<{ customerId: string }>();
  const { t } = useTranslation("menuBar");
  const label = t(item.labelKey);

  const basePath = `/customers/${customerId}/v2/${item.path}`;
  const isActive = location.pathname.startsWith(basePath);

  const handleClick = () => {
    navigate(basePath);
  };

  const itemContent = (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: expanded ? 1.5 : 0,
        px: expanded ? 1.5 : 0,
        justifyContent: expanded ? "flex-start" : "center",
        height: 40,
        mx: 0.5,
        borderRadius: "8px",
        cursor: "pointer",
        position: "relative",
        borderLeft: isActive ? "2px solid var(--accent)" : "2px solid transparent",
        backgroundColor: isActive ? "var(--accent-dim)" : "transparent",
        color: isActive ? "var(--accent)" : "var(--text-secondary)",
        transition: "background-color 150ms ease, color 150ms ease",
        "&:hover": {
          backgroundColor: isActive ? "var(--accent-dim)" : "var(--bg-hover)",
          color: isActive ? "var(--accent)" : "var(--text-primary)",
        },
        overflow: "hidden",
        whiteSpace: "nowrap",
      }}
    >
      <Box
        component="svg"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
        sx={{
          width: 18,
          height: 18,
          flexShrink: 0,
        }}
      >
        <path d={item.icon} />
      </Box>
      {expanded && (
        <Box
          component="span"
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 500,
            letterSpacing: "0.01em",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {label}
        </Box>
      )}
    </Box>
  );

  if (!expanded) {
    return (
      <Tooltip title={label} placement="right" arrow>
        {itemContent}
      </Tooltip>
    );
  }

  return itemContent;
}
